import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Mono,
  PageHeader,
  ResourceTable,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '@verydia/ui';
import type { ProductResource } from '@verydia/contracts';
import { Package, Plus } from 'lucide-react';
import { useState } from 'react';
import { rootRoute } from './root.tsx';
import { describeFailure, isStarting } from '../lib/api.ts';
import { useConsoleContext } from '../lib/console-context.tsx';
import { useCreateProduct, useProducts } from '../lib/identity-queries.ts';
import { DISTRIBUTIONS, distributionLabel } from '../components/distribution.ts';

const STATUS_TONE = { ACTIVE: 'success', ARCHIVED: 'neutral' } as const;

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Mirrors `slugFromProductName` on the server, so the preview is the real thing. */
function previewSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function CreateProductDialog({
  organizationId,
  open,
  onOpenChange,
  onCreated,
}: {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (product: ProductResource) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  /**
   * Whether the operator has taken the slug over.
   *
   * Until they do, typing a name fills the slug field so the real value is
   * visible and editable rather than hidden behind a placeholder. Once they
   * edit it, their text is theirs — a name change must not silently rewrite a
   * slug somebody deliberately chose.
   */
  const [slugEdited, setSlugEdited] = useState(false);
  const [distribution, setDistribution] = useState('');
  const [description, setDescription] = useState('');
  const create = useCreateProduct(organizationId);

  const failure = create.isError ? describeFailure(create.error) : null;

  const close = () => {
    create.reset();
    setName('');
    setSlug('');
    setSlugEdited(false);
    setDistribution('');
    setDescription('');
    onOpenChange(false);
  };

  const onNameChange = (value: string) => {
    setName(value);
    // Mirror into the slug until the operator claims that field.
    if (!slugEdited) setSlug(previewSlug(value));
  };

  // Distribution has no default: the vendor states how their software ships.
  // Slug is genuinely required — it was marked with an asterisk while a blank
  // value was quietly accepted and derived, which is a contract the form and
  // the server did not share.
  const canSubmit =
    name.trim().length > 0 && slug.trim().length > 0 && distribution !== '' && !create.isPending;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        name: name.trim(),
        distribution,
        slug: slug.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      {
        onSuccess: (result) => {
          onCreated(result.data);
          close();
        },
      },
    );
  };

  // A conflict is about one field; anything else belongs at the form level.
  const slugConflict = failure !== null && /already uses/i.test(failure.message);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Create a product"
      description="A product is a piece of software you license through Verydia Control."
      footer={
        <>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Plus />} disabled={!canSubmit} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create product'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field
          label="Product name"
          required
          description="The name people see. It can be changed later."
          error={!slugConflict && failure ? failure.message : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Slug"
          required
          description="Used in URLs and API paths, and unique within this organization. Permanent once set."
          error={slugConflict ? failure!.message : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              value={slug}
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label="Distribution" required description="How this software is delivered or run.">
          {(props) => (
            <Select
              {...props}
              value={distribution}
              onChange={(event) => setDistribution(event.target.value)}
            >
              {/*
                Deliberately unselected until chosen. Preselecting SaaS would put
                an assumption of ours into the vendor's catalog, and it is wrong
                for most of the software this platform exists to license.
              */}
              <option value="">Select a distribution…</option>
              {DISTRIBUTIONS.map((value) => (
                <option key={value} value={value}>
                  {distributionLabel(value)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Description" description="Optional.">
          {(props) => (
            <TextInput
              {...props}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        {failure?.requestId ? (
          <p className="text-[11.5px] text-[var(--vc-text-subtle)]">
            Request <Mono>{failure.requestId}</Mono>
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function ProductsPage() {
  const context = useConsoleContext();
  const navigate = useNavigate();
  const organizationId = context.organization?.id ?? null;
  const products = useProducts(organizationId);
  const [createOpen, setCreateOpen] = useState(false);

  const columns: ReadonlyArray<Column<ProductResource>> = [
    {
      key: 'product',
      header: 'Product',
      render: (row) => (
        <Link
          to="/products/$productId"
          params={{ productId: row.id }}
          className="flex flex-col hover:underline"
        >
          <span className="font-medium text-[var(--vc-text)]">{row.name}</span>
          <Mono className="text-[12px] text-[var(--vc-text-subtle)]">{row.slug}</Mono>
        </Link>
      ),
    },
    {
      key: 'distribution',
      header: 'Distribution',
      width: 'w-32',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{distributionLabel(row.distribution)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-28',
      render: (row) => <StatusBadge tone={STATUS_TONE[row.status]}>{row.status}</StatusBadge>,
    },
    {
      key: 'environments',
      header: 'Environments',
      align: 'right',
      width: 'w-32',
      render: (row) => (
        <span className="tabular-nums text-[var(--vc-text-muted)]">{row.environment_count}</span>
      ),
    },
    {
      key: 'plans',
      header: 'Licensed plans',
      align: 'right',
      width: 'w-32',
      render: (row) => (
        <span className="tabular-nums text-[var(--vc-text-muted)]">
          {row.licensed_product_plan_count}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      width: 'w-32',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{formatDate(row.updated_at)}</span>
      ),
    },
  ];

  const failure = products.isError ? describeFailure(products.error) : null;
  const starting = products.isFetching && isStarting(products.failureReason ?? products.error);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Products"
        description={`Software ${context.organization?.name ?? 'this organization'} licenses through Verydia Control.`}
        breadcrumbs={[
          { label: context.organization?.name ?? 'Organization', href: '/' },
          { label: 'Products' },
        ]}
        linkComponent={Link}
        actions={
          organizationId !== null ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
              Create product
            </Button>
          ) : null
        }
      />

      <SectionCard flush>
        {/*
          Loading, starting, error and empty are four different answers and must
          not collapse into one. Showing "No products yet" while the request is
          still in flight is a lie the user has no way to detect.
        */}
        {starting ? (
          <LoadingState label="Verydia is starting up" />
        ) : products.isPending ? (
          <LoadingState label="Loading products" />
        ) : failure !== null ? (
          <ErrorState
            title="Could not load products"
            description={failure.message}
            requestId={failure.requestId}
            onRetry={() => void products.refetch()}
          />
        ) : (
          <ResourceTable
            caption="Products in this organization"
            columns={columns}
            rows={products.data?.data.data ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                icon={<Package />}
                title="No products yet"
                description="Create your first product to begin defining its licensing catalog."
                action={
                  <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
                    Create product
                  </Button>
                }
              />
            }
          />
        )}
      </SectionCard>

      {organizationId !== null ? (
        <CreateProductDialog
          organizationId={organizationId}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(product) => {
            void navigate({ to: '/products/$productId', params: { productId: product.id } });
          }}
        />
      ) : null}
    </div>
  );
}

export const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  component: ProductsPage,
});
